import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../utils/AuthContext';
import { createPaymentIntent, calculateBookingCosts, formatPrice } from '../utils/stripeConfig';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51RnkA8RUcr7bTSlQbG2GOWEd6IdlCesVaqtgGihLg7VoDkKZTDS2IyMZ3L3mFGljaHUMnZw3BBmh79nVn1puLYG700lIZrwben');

const CheckoutContainer = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.1);
`;

const BookingSummary = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const PaymentForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const CardElementContainer = styled.div`
  padding: 1rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  background: white;
  
  &:focus-within {
    border-color: #0d9488;
  }
`;

const PriceBreakdown = styled.div`
  border-top: 1px solid #e1e5e9;
  padding-top: 1rem;
  margin-top: 1rem;
  
  .row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    
    &.total {
      font-weight: bold;
      font-size: 1.1rem;
      border-top: 1px solid #e1e5e9;
      padding-top: 0.5rem;
      margin-top: 0.5rem;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const CheckoutForm: React.FC<{ bookingData: any }> = ({ bookingData }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const costs = calculateBookingCosts(
    bookingData.basePrice,
    bookingData.nights
  );

  useEffect(() => {
    // Crea payment intent quando il componente si monta
    const createIntent = async () => {
      try {
        const result = await createPaymentIntent({
          amount: Math.round(costs.total * 100), // Stripe usa centesimi
          currency: 'eur',
          bookingId: bookingData.bookingId || 'temp',
          userId: user?.id || '',
          metadata: {
            accommodationId: bookingData.accommodationId?.toString() || '',
            checkIn: bookingData.checkIn || '',
            checkOut: bookingData.checkOut || '',
            guestName: bookingData.guestName || '',
            guestEmail: bookingData.guestEmail || ''
          }
        });

        if (result.success && result.clientSecret) {
          setClientSecret(result.clientSecret);
        } else {
          setError(result.error || 'Errore nella creazione del pagamento');
        }
      } catch (err: any) {
        setError(err.message || 'Errore imprevisto');
      }
    };

    createIntent();
  }, [bookingData, costs.total, user?.id]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setError('Sistema di pagamento non disponibile');
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Elemento carta non trovato');
      setProcessing(false);
      return;
    }

    const { error: paymentError, paymentIntent } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: bookingData.guestName || user?.email || '',
            email: bookingData.guestEmail || user?.email || '',
          },
        },
      }
    );

    if (paymentError) {
      setError(paymentError.message || 'Errore nel pagamento');
      setProcessing(false);
    } else if (paymentIntent?.status === 'succeeded') {
      // Pagamento riuscito - reindirizza alla pagina di conferma
      navigate('/booking-confirmation', {
        state: {
          bookingData,
          paymentIntentId: paymentIntent.id,
          success: true
        }
      });
    }
  };

  return (
    <CheckoutContainer>
      <h1>Completa il Pagamento</h1>
      
      <BookingSummary>
        <h3>Riepilogo Prenotazione</h3>
        <p><strong>Alloggio:</strong> {bookingData.accommodationTitle}</p>
        <p><strong>Check-in:</strong> {new Date(bookingData.checkIn).toLocaleDateString('it-IT')}</p>
        <p><strong>Check-out:</strong> {new Date(bookingData.checkOut).toLocaleDateString('it-IT')}</p>
        <p><strong>Ospiti:</strong> {bookingData.guestsCount}</p>
        <p><strong>Notti:</strong> {costs.nights}</p>
        
        <PriceBreakdown>
          <div className="row">
            <span>€{bookingData.basePrice} x {costs.nights} notti</span>
            <span>{formatPrice(costs.subtotal)}</span>
          </div>
          <div className="row">
            <span>Pulizia</span>
            <span>{formatPrice(costs.cleaningFee)}</span>
          </div>
          <div className="row">
            <span>Commissione servizio</span>
            <span>{formatPrice(costs.serviceFee)}</span>
          </div>
          <div className="row">
            <span>Tasse</span>
            <span>{formatPrice(costs.taxes)}</span>
          </div>
          <div className="row total">
            <span>Totale</span>
            <span>{formatPrice(costs.total)}</span>
          </div>
        </PriceBreakdown>
      </BookingSummary>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <PaymentForm onSubmit={handleSubmit}>
        <div>
          <label>Informazioni Carta di Credito</label>
          <CardElementContainer>
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                },
              }}
            />
          </CardElementContainer>
        </div>

        <Button
          type="submit"
          disabled={!stripe || processing || !clientSecret}
          style={{
            backgroundColor: processing ? '#6b7280' : '#0d9488',
            padding: '1rem 2rem',
            fontSize: '1.1rem'
          }}
        >
          {processing ? 'Elaborazione...' : `Paga ${formatPrice(costs.total)}`}
        </Button>
      </PaymentForm>

      <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
        <p>🔒 I tuoi dati di pagamento sono protetti con crittografia SSL</p>
        <p>Powered by Stripe</p>
      </div>
    </CheckoutContainer>
  );
};

const Checkout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const bookingData = location.state?.bookingData;

  if (!bookingData) {
    return (
      <Layout>
        <CheckoutContainer>
          <h1>Errore</h1>
          <p>Dati di prenotazione non trovati. Torna alla pagina di prenotazione.</p>
          <Button onClick={() => navigate('/accommodations')}>
            Torna agli Alloggi
          </Button>
        </CheckoutContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <Elements stripe={stripePromise}>
        <CheckoutForm bookingData={bookingData} />
      </Elements>
    </Layout>
  );
};

export default Checkout;